import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Accordion, Popup, Icon, Grid, Menu, Transition, Image } from 'semantic-ui-react'
import { Link } from 'react-router-dom'
import _ from 'lodash'

import './Sidebar.scss'
import styles from './../../styles/global.module.scss'
import { connect } from 'react-redux'
import clientActions from '../../store/actions/clients'
import icons from '../Icons'
import { useStore } from 'react-redux'; //debug para logo
import { useUniversalLateralMenuHook } from 'hooks/useUniversalLateralMenuHook'
import { isTabSwitchNavigation, useTabNavigation } from 'hooks/useTabNavigation'



function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window
    return { width, height }
}

const isImageIcon = (v) =>
    typeof v === 'string' && (/^https?:\/\//.test(v) || v.startsWith('/') || v.startsWith('data:'))

const normalizeGroup = (s = '') =>
    (s ?? '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
// helper para quitar query/hash antes de normalizar rutas
const stripQueryHash = (s = '') => String(s).split('#')[0].split('?')[0]

// Helper para normalizar paths (quita query/hash y normaliza)
const normalizePath = (s = '/') => {
    const clean = stripQueryHash(s || '/').toLowerCase().trim()
    const withSlash = ('/' + clean).replace(/\/+/g, '/')
    return withSlash !== '/' ? withSlash.replace(/\/$/, '') : '/'
}
const getRawPath = (loc) => {
    if (typeof loc?.hash === 'string' && loc.hash.startsWith('#')) {
        return loc.hash.slice(1) // sin '#'
    }
    return loc?.pathname || '/'
}
const stripBasePrefix = (pathname) => {
    const norm = normalizePath(pathname)
    const parts = norm.split('/') // ['', 'dashboard', 'modulo', 'proceso'...]
    const baseSet = new Set(['dashboard', 'home', 'inicio'])
    return (parts.length > 2 && baseSet.has(parts[1])) ? '/' + parts.slice(2).join('/') : norm
}
const equalsOrParentOf = (current, target) =>
    current === target || current.startsWith(target + '/')

// groupIconMap será creado dentro del componente con useMemo (hooks solo dentro)
const ClientSidebarMenu = (props) => {
    const { t } = useTranslation()
    const [companyLogoSrc, setCompanyLogoSrc] = useState(props.client?.sysCompanyImage?.logo_url || null)
    const [logoDimensions, setLogoDimensions] = useState({
        menu: null,
        default: null
    })
    const companyLogoFrameRef = useRef(null)
    const closeStickyRef = useRef(null)
    const [stickyOffset, setStickyOffset] = useState(95)

    // Desestructurar props usadas en el componente (evita errores no-undef)
    const {
        expand,
        setExpand,
        client,
        location,
        mobileOnly,
        setMobileOnly,
        onMenuOpen,
        onTabOpen,
        lang,
        // si usas otras props agrégalas aquí
    } = props
    // --- calcular ruta actual usando la location de props (soporte HashRouter) ---
    const rawPath = (typeof location?.hash === 'string' && location.hash.startsWith('#'))
        ? location.hash.slice(1)
        : (location?.pathname || window.location.pathname || '/')
    const currentPath = normalizePath(rawPath)
    const appPath = stripBasePrefix(rawPath)
    const shouldPreserveSidebarState = isTabSwitchNavigation(location)
const groupIconMap = useMemo(() => {
        const map = new Map()
            ; (icons || []).forEach((i) => map.set(normalizeGroup(i.title), i.url))
        return map
    }, [])

    const { tabs: openTabs, activeTabId } = useTabNavigation();

    // estados locales necesarios para reproducir el comportamiento del SidebarMenu
    // lateralMenu ahora viene del hook con persistencia en backend
    const { lateralMenu } = useUniversalLateralMenuHook('client');
    const [previousExpand, setPreviousExpand] = useState(null)

    // Sincronizar lateralMenu con expand en TIEMPO REAL
    useEffect(() => {
        if (mobileOnly || lateralMenu === null) return;
        if (lateralMenu === true) {
            setExpand(1);
        } else if (lateralMenu === false) {
            if (typeof onTabOpen !== 'function') setExpand(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lateralMenu]);

    const underlineMenu = false

    const getLogoFitScore = (naturalWidth, naturalHeight, frameWidth, frameHeight) => {
        if (!naturalWidth || !naturalHeight || !frameWidth || !frameHeight) return -1

        const containScale = Math.min(frameWidth / naturalWidth, frameHeight / naturalHeight)
        const renderedWidth = naturalWidth * containScale
        const renderedHeight = naturalHeight * containScale
        const widthRatio = renderedWidth / frameWidth
        const heightRatio = renderedHeight / frameHeight
        const usedRatio = Math.max(widthRatio, heightRatio)

        const verticalPenalty = heightRatio > 0.94 ? (heightRatio - 0.94) * 0.15 : 0
        const compactPenalty = widthRatio < 0.56 ? (0.56 - widthRatio) * 0.35 : 0
        return usedRatio - verticalPenalty - compactPenalty
    }

    const medirLogo = (tipo, img) => {
        if (!img?.naturalWidth || !companyLogoFrameRef?.current) return

        const frameEl = companyLogoFrameRef.current
        const frameWidth = frameEl.clientWidth || 0
        const frameHeight = frameEl.clientHeight || 0

        if (!frameWidth || !frameHeight) return

        setLogoDimensions(prev => ({
            ...prev,
            [tipo]: {
                width: img.naturalWidth,
                height: img.naturalHeight
            }
        }))
    }

    const marcarError = (tipo) => {
        setLogoDimensions(prev => ({
            ...prev,
            [tipo]: { width: 0, height: 0 }
        }))
    }

    useEffect(() => {
        const menuLogo = client?.sysCompanyImage?.menu_logo_url || null
        const defaultLogo = client?.sysCompanyImage?.logo_url || null

        if (!menuLogo && !defaultLogo) {
            setCompanyLogoSrc(null)
            return
        }

        if (!menuLogo) {
            setCompanyLogoSrc(defaultLogo)
            return
        }

        if (!defaultLogo) {
            setCompanyLogoSrc(menuLogo)
            return
        }

        if (!logoDimensions.menu || !logoDimensions.default) {
            setCompanyLogoSrc(menuLogo)
            return
        }

        const frameEl = companyLogoFrameRef?.current
        const frameWidth = frameEl?.clientWidth || 0
        const frameHeight = frameEl?.clientHeight || 0

        if (!frameWidth || !frameHeight) {
            setCompanyLogoSrc(menuLogo)
            return
        }

        const scoreMenu = getLogoFitScore(
            logoDimensions.menu.width,
            logoDimensions.menu.height,
            frameWidth,
            frameHeight
        )

        const scoreDefault = getLogoFitScore(
            logoDimensions.default.width,
            logoDimensions.default.height,
            frameWidth,
            frameHeight
        )

        setCompanyLogoSrc(scoreMenu >= scoreDefault ? menuLogo : defaultLogo)
    }, [
        client?.sysCompanyImage?.menu_logo_url,
        client?.sysCompanyImage?.logo_url,
        logoDimensions,
        expand,
    ])

    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions())
    const [availableRoutes, setAvailableRoutes] = useState([])
    const [allowedRoutes, setAllowedRoutes] = useState([])

    // Accordion open indices (uno por módulo)
    const [openHeaderIndices, setOpenHeaderIndices] = useState([])
    const [openSubHeaderTitles, setOpenSubHeaderTitles] = useState([]) // <-- añadido: sub-headers abierto
    // s
    const [openChildRoutes, setOpenChildRoutes] = useState([]) // <-- sub-dropdowns dentro de un módulo

    const activeItemRef = useRef(null)
    const containerRef = useRef(null) // <- contenedor scrollable del sidebar
    const manualToggleForPath = useRef(null)
    const initialCollapseApplied = useRef(false) // Bandera para aplicar colapso solo una vez

    // fetch modules + permissions (como tenías)
    useEffect(() => {
        componentDidMount()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const componentDidMount = async () => {
        try {
            const clientId = client?.id
            const promises = [props.getClientModules()]
            promises.push(clientId ? props.getWebClientPermissions(clientId) : Promise.resolve([]))
            const [av, allowed] = await Promise.all(promises)
            setAvailableRoutes(av || [])
            setAllowedRoutes(allowed || [])
        } catch (err) {
            console.error('ClientSidebarMenu load error', err)
            setAvailableRoutes([])
            setAllowedRoutes([])
        }
    }

    // resize
    useEffect(() => {
        function handleResize() {
            const dims = getWindowDimensions()
            if (dims.width !== windowDimensions.width || dims.height !== windowDimensions.height) {
                setWindowDimensions(dims)
            }
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [windowDimensions])

    // mobile behavior
    useEffect(() => {
        if (windowDimensions.width <= 425) {
            setMobileOnly(true)
            if (expand === 1 || expand == null) setExpand(1)
        } else {
            setMobileOnly(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowDimensions])

    useEffect(() => {
        const updateStickyOffset = () => {
            if (!closeStickyRef.current) return
            const nextOffset = closeStickyRef.current.offsetHeight || 0
            if (nextOffset > 0) setStickyOffset(nextOffset)
        }

        const id = window.requestAnimationFrame(updateStickyOffset)
        window.addEventListener('resize', updateStickyOffset)
        return () => {
            window.cancelAnimationFrame(id)
            window.removeEventListener('resize', updateStickyOffset)
        }
    }, [expand, companyLogoSrc, windowDimensions.width])

    // Mantener estado de expansión al navegar (solo asegurar visible en móvil, igual que SidebarMenu)
    useEffect(() => {
        if (mobileOnly) setExpand(1)
    }, [location, mobileOnly])

    // Construir headers/headersRoutes a partir de availableRoutes + allowedRoutes (memoizado)
    const modulesToRender = useMemo(() => {
        if (!availableRoutes || !availableRoutes.length) return [];

        return availableRoutes
            .map((module) => {
                const moduleName = Object.keys(module)[0];
                const data = module[moduleName] || {};
                const filteredRoutes = (data.routes || [])
                    .filter(r => !r.is_tab)
                    .filter(r => {
                        // Incluir si la ruta está permitida, O si tiene children permitidos
                        if (allowedRoutes.includes(r.url)) return true;
                        if (Array.isArray(r.children) && r.children.some(c => allowedRoutes.includes(c.url))) return true;
                        return false;
                    })
                    .map(r => ({
                        ...r,
                        module_group: moduleName,
                        // Filtrar children para mostrar solo los permitidos
                        children: Array.isArray(r.children)
                            ? r.children.filter(c => allowedRoutes.includes(c.url))
                            : [],
                    }));

                return {
                    module_group: moduleName,
                    module_group_en: data.name_en,
                    module_group_pt: data.name_pt,
                    module_group_ar: data.name_ar,
                    icon: data.icon,
                    routes: filteredRoutes,
                };
            })
            .filter(Boolean);
    }, [availableRoutes, allowedRoutes]);

    const headers = useMemo(() => {
        return modulesToRender.reduce((accu, module) => {
            const x = accu.find((item) => item.module_group == module.module_group)
            return x ? accu : accu.concat([module])
        }, [])
    }, [modulesToRender])
    // --- DEFINICIÓN ÚNICA DE RUTA ACTUAL Y helper isActiveRoute (evita duplicados) ---
    //const currentPath = normalizePath((location && location.pathname) || window.location.pathname || '/')
    // rawPath soporta HashRouter (usa location.hash si existe)
const isActiveRoute = (to) => {
        if (!to) return false
        const target = normalizePath(`/${to}`) // normaliza el to
        return equalsOrParentOf(appPath, target) || equalsOrParentOf(currentPath, target)
    }
    const normalizeTabPath = (path) => {
        const normalized = `/${path || ''}`.replace(/\/+/g, '/')
        return normalized === '/' ? '/' : normalized.replace(/\/$/, '')
    }
    const isRouteInOpenTab = (routePath) => {
        if (!openTabs || !Array.isArray(openTabs)) return false
        const normalized = normalizeTabPath(routePath)
        return openTabs.some(tab => normalizeTabPath(tab.path) === normalized)
    }
    const isRouteInActiveTab = (routePath) => {
        if (!openTabs || !activeTabId) return false
        const normalized = normalizeTabPath(routePath)
        const activeTab = openTabs.find(tab => tab.id === activeTabId)
        return activeTab && normalizeTabPath(activeTab.path) === normalized
    }
    // rutas planas (normalizadas para uso en filtros y en el BentoGrid)
    const routes = React.useMemo(() => {
        return modulesToRender
            .map((m) => {
                if (!m || !m.routes) return null
                const iconValue = m.icon || m.menuIcon || m.fa_icon || null
                return (m.routes || []).map((r) => ({
                    ...r,
                    title: r.name || r.title || r.url,
                    to: r.url,
                    group: m.module_group,
                    groupKey: normalizeGroup(m.module_group),
                    sub_group: r.sub_group || r.subGroup || null,
                    subGroupKey: normalizeGroup(r.sub_group || r.subGroup || ''),
                    icon: r.icon || null,
                    children: Array.isArray(r.children) ? r.children.map(c => ({ ...c, icon: c.icon || null })) : [],
                }))
            })
            .flat()
            .filter(Boolean)
    }, [modulesToRender])

    // índice del header y subgrupo activos (derivado, no state)
    const activeRoute = useMemo(() => routes.find((r) => isActiveRoute(r.url, r.group)), [routes, currentPath])
    const activeHeaderIndex = useMemo(() => {
        if (!activeRoute) return -1
        return modulesToRender.findIndex((h) => h.module_group === activeRoute.group)
}, [modulesToRender, activeRoute])
    const activeSubGroup = activeRoute?.sub_group ?? null
    const activeGroup = activeRoute?.group || null
    // Helpers de "profundidad" — devuelven clases que tu SCSS ya usa
    // Header: cuando está abierto se marca como 'depth-parent' (se encoge)
    const getHeaderDepthClass = (headerIndex) =>
        openHeaderIndices.includes(headerIndex) ? 'depth-parent' : ''

    // Ruta: si es la ruta activa -> depth-current; si su header está abierto -> depth-parent
    const getRouteDepthClass = (route, headerIndex) => {
        if (isActiveRoute(route.url)) return 'depth-current'
        return openHeaderIndices.includes(headerIndex) ? 'depth-parent' : ''
    }


    // current path (normalizado)


    // Forzar sidebar contraído al entrar al dashboard (caso post-login) - SOLO UNA VEZ
    useEffect(() => {
        if (shouldPreserveSidebarState) return
        // Solo ejecutar si aún no se ha aplicado el colapso inicial
        if (initialCollapseApplied.current) return

        // Detectar entrada al "inicio" de la app (ruta que suele abrirse después del login)
        const isEntryPoint =
            appPath === '/' ||
            currentPath === '/' ||
            rawPath === '/client/client_dashboard' ||
            rawPath === '/client' ||
            rawPath === '/dashboard' ||
            rawPath === '/home' ||
            rawPath === '/inicio'

        if (isEntryPoint) {
            // cerrar cualquier header/subheader abierto y evitar auto-open inmediato
            setOpenHeaderIndices([])
            setOpenSubHeaderTitles([])
            manualToggleForPath.current = currentPath
            // Marcar que ya se aplicó el colapso inicial (no volver a ejecutar)
            initialCollapseApplied.current = true
        }
        // Ejecutar solo al montar y cuando cambia la ruta (pero la bandera evita re-ejecución)
    }, [location?.pathname, location?.hash, shouldPreserveSidebarState])

    // Auto-open header/subgroup de la ruta activa (respeta toggle manual en la misma ruta)
    useEffect(() => {
        if (expand === 0) return
        if (shouldPreserveSidebarState) return
        if (!routes?.length || !headers?.length) return

        // evitar auto-open si el usuario hizo toggle manual en la misma ruta
        if (manualToggleForPath.current === currentPath) {
            manualToggleForPath.current = null
            return
        }

        const active = routes.find(r => isActiveRoute(r.url))
        if (!active) return

        const hIndex = headers.findIndex(h => h.module_group === active.group)
        if (hIndex >= 0) {
            setOpenHeaderIndices(prev => (prev.includes(hIndex) ? prev : [...prev, hIndex]))
        }
        if (active.sub_group) {
            setOpenSubHeaderTitles(prev => (prev.includes(active.sub_group) ? prev : [...prev, active.sub_group]))
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPath, routes, headers, expand, shouldPreserveSidebarState])

    // Auto-open child routes que tengan un hijo activo (solo al cargar rutas, no bloquea el toggle)
    useEffect(() => {
        if (!routes?.length) return
        routes.forEach(route => {
            if (!Array.isArray(route.children) || route.children.length === 0) return
            const anyChildActive = route.children.some(c => isActiveRoute(c.url))
            if (anyChildActive) {
                const key = `${route.group}-${route.title}`
                setOpenChildRoutes(prev => prev.includes(key) ? prev : [...prev, key])
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routes])


    useEffect(() => {
        if (shouldPreserveSidebarState) return
        const t = setTimeout(() => {
            const el = activeItemRef.current
            const container = containerRef.current
            if (!el || !container) return

            try {
                // elemento y contenedor en pantalla
                const elRect = el.getBoundingClientRect()
                const contRect = container.getBoundingClientRect()

                // Si el elemento está fuera del área visible del contenedor, ajustar scroll
                if (elRect.top < contRect.top || elRect.bottom > contRect.bottom) {
                    // calcular posición de scroll relativa al contenedor (centra el elemento)
                    const offset = el.offsetTop - container.clientHeight / 2 + el.clientHeight / 2
                    container.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' })
                } else {
                    // si ya está visible, opcionalmente alinearlo al borde más cercano (suave)
                    // container.scrollTo({ top: container.scrollTop, behavior: 'smooth' })
                }
            } catch (e) {
                // fallback simple
                try { el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }) } catch { el.scrollIntoView() }
            }
        }, 260)
        return () => clearTimeout(t)
    }, [currentPath, expand, openHeaderIndices.join(','), shouldPreserveSidebarState, /* si usas subheaders incluirlo */])

    // helpers para recolectar rutas según header o subheader
    const collectRoutesFor = (opts = {}) => {
        if (!Array.isArray(routes)) return []
        if (opts.subTitle) {
            const targetSub = normalizeGroup(opts.subTitle)
            return routes
                .filter(r => normalizeGroup(r.sub_group) === targetSub)
                .map(r => ({ ...r, icon: r.icon || null, children: (r.children || []).map(c => ({ ...c, icon: c.icon || null })) }))
        }
        if (opts.headerKey) {
            const targetGroup = normalizeGroup(opts.headerKey)
            return routes
                .filter(r => normalizeGroup(r.group) === targetGroup)
                .map(r => ({ ...r, icon: r.icon || null, children: (r.children || []).map(c => ({ ...c, icon: c.icon || null })) }))
        }
        return []
    }

    // handlers
    const handleHeaderClick = (event, title) => {
        event.preventDefault()
        event.stopPropagation()
        const { index } = title
        manualToggleForPath.current = currentPath

        const isOpening = !openHeaderIndices.includes(index)
        setOpenHeaderIndices(prev => (prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]))

        const header = headers[index]
        if (!header) return

        if (typeof onMenuOpen === 'function') {
            const headerKey = header.module_group

            if (isOpening) {
                // Trae TODO lo que pertenezca a este grupo
                const routesForHeader = collectRoutesFor({ headerKey: headerKey })
                // debug: emitir payload al abrir header
                console.debug && console.debug('[ClientSidebarMenu] onMenuOpen header', { headerKey, routesForHeader })
                /* 
                   Comentado para evitar que el panel central (BentoGrid) cambie automáticamente 
                   al Dashboard o muestre contenido al solo expandir el menú lateral.
                */
                /*
                onMenuOpen({
                    type: 'header',
                    key: normalizeGroup(headerKey),
                    header,
                    routes: routesForHeader
                })
                */
            } else {
                // Al cerrar el grupo principal, limpiamos el BentoGrid (volver a Recientes)
                onMenuOpen(null)
            }
        }
    }

    const handleSubHeaderClick = (event, subTitle) => {
        event.preventDefault()
        event.stopPropagation() // ¡Muy importante! Evita que el clic suba al Header

        manualToggleForPath.current = currentPath

        const isOpening = !openSubHeaderTitles.includes(subTitle)

        // Actualizar estado visual del acordeón
        setOpenSubHeaderTitles(prev => (prev.includes(subTitle) ? prev.filter(t => t !== subTitle) : [...prev, subTitle]))

        // LÓGICA DE ENVÍO AL BENTOGRID
        if (typeof onMenuOpen === 'function') {
            if (isOpening) {
                // 1. Recolectamos SOLO las rutas de este subgrupo
                const routesForSub = collectRoutesFor({ subTitle })
                // 2. Enviamos al BentoGrid
                // debug: emitir payload al abrir subheader
                console.debug && console.debug('[ClientSidebarMenu] onMenuOpen subheader', { subTitle, routesForSub })
                onMenuOpen({
                    type: 'subheader',
                    key: normalizeGroup(subTitle),
                    subTitle,
                    routes: routesForSub
                })
            } else {
                // Si estamos cerrando el submenú, intentamos mostrar las opciones del grupo padre
                const parentGroup = routes.find(r => normalizeGroup(r.sub_group) === normalizeGroup(subTitle))?.group

                if (parentGroup) {
                    const routesForHeader = collectRoutesFor({ headerKey: parentGroup })
                    // debug: al cerrar subheader volver a header (payload)
                    console.debug && console.debug('[ClientSidebarMenu] onMenuOpen restoring header', { parentGroup, routesForHeader })
                    onMenuOpen({
                        type: 'header',
                        key: normalizeGroup(parentGroup),
                        header: headers.find(h => h.module_group === parentGroup),
                        routes: routesForHeader
                    })
                } else {
                    console.debug && console.debug('[ClientSidebarMenu] onMenuOpen clear (no parent)')
                    onMenuOpen(null)
                }
            }
        }
    }

    // cuando se hace click en una ruta sin subgrupo, emitir esa ruta (útil para el panel)
    const handleRouteClickEmit = (route) => {
        if (typeof onMenuOpen === 'function') {
            console.debug && console.debug('[ClientSidebarMenu] onMenuOpen route', { route })
            onMenuOpen({
                type: 'route',
                key: route.key || route.to || route.title,
                route,
                routes: [{ ...route, icon: route.icon || null }],
            })
        }
    }

    const handleClickLink = (route) => {
        props.actionClick(route)
    }

    const handleTabOpen = (e, route) => {
        if (typeof onTabOpen === 'function') {
            e.preventDefault()
            handleClickLink(route)
            onTabOpen(route)
        }
    }


    // render
    return (
        <div
            className='sidebar'
            ref={containerRef}
            style={{
                width: (expand == null || expand == 1) ? '225px' : '0px',
                overflowY: 'auto',
                overflowX: 'hidden',
            }}
        >
            {/* Logo + botón hamburguesa (mismo layout que SidebarMenu de usuario) */}
            {(expand == null || expand == 1) && (
                <div className='Sidebar__CloseStickyy' ref={closeStickyRef} style={{ padding: 0, margin: 0 }}>
                    <div className='Sidebar__Header'>
<div className='Sidebar__MenuButtonRow' style={{justifyContent:'flex-start',paddingLeft:'0',paddingRight:'0'}}>
                            <button
                                className='Sidebar__MenuButton Sidebar__MenuButton--shell'
                                onClick={() => {
                                    if (lateralMenu && !mobileOnly) {
                                        return
                                    }

                                    setExpand((previousExpandedState) => {
                                        setPreviousExpand(previousExpandedState)
                                        const newExpandState = !mobileOnly
                                            ? previousExpandedState == 1
                                                ? (previousExpand == null || previousExpand == 2) ? 0 : 2
                                                : 1
                                            : !previousExpandedState ? 1 : 0

                                        return newExpandState
                                    })
                                }}
                                aria-label={t('Abrir/cerrar menú lateral')}
                                style={{background:'transparent',border:'1.1px solid #6c6c6c',borderRadius:7,padding:'0 13px 0 3px',height:26,minWidth:62,display:'flex',alignItems:'center',gap:3,boxShadow:'none',justifyContent:'flex-start',textAlign:'left'}}
                            >
                                <img
                                    src='https://img.icons8.com/?size=100&id=100033&format=png&color=000000'
                                    alt='Menu'
                                    style={{width:14.5,height:14.5,marginRight:-6,marginLeft:0,display:'inline-block'}}
                                />
                                <span style={{fontSize:13,color:'#6c6c6c',fontWeight:500,whiteSpace:'nowrap',marginLeft:0}}>ocultar</span>
                            </button>
                        </div>

                        <div className='Sidebar__LogoRow'>
                            <div className='Sidebar__LogoSlot'>
                                <div className='Sidebar__LogoFrame' ref={companyLogoFrameRef}>
                                    {/* Logos ocultos para medir */}
                                    {client?.sysCompanyImage?.menu_logo_url && (
                                        <img
                                            src={client.sysCompanyImage.menu_logo_url}
                                            style={{ display: 'none' }}
                                            onLoad={(e) => medirLogo('menu', e.target)}
                                            onError={() => marcarError('menu')}
                                        />
                                    )}
                                    {client?.sysCompanyImage?.logo_url && (
                                        <img
                                            src={client.sysCompanyImage.logo_url}
                                            style={{ display: 'none' }}
                                            onLoad={(e) => medirLogo('default', e.target)}
                                            onError={() => marcarError('default')}
                                        />
                                    )}

                                    {/* Logo visible */}
                                    {companyLogoSrc ? (
                                        <img
                                            src={companyLogoSrc}
                                            alt={t('Company Logo')}
                                            className='Sidebar__CompanyLogo SideBar__Outline'
                                            onClick={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        />
                                    ) : (
                                        <div className='Sidebar__LogoPlaceholder' />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <Transition visible={expand == null || expand == 1} animation='fade down' duration={220} unmountOnHide={false}>
                <div style={{ marginTop: stickyOffset }}>
                    {modulesToRender.map((header, index) => (
                        header.routes?.length > 0 ? <Accordion key={`accordion-${header.module_group}-${index}`} fluid style={{ paddingLeft: 12, paddingBottom: 5 }}>
                            <Accordion.Title
                                key={`title-${header.module_group}-${index}`}
                                active={openHeaderIndices.includes(index)}
                                index={index}
                                // onClick={(e, t) => handleHeaderClick(e, { index })}
                                // className={''}
                                onClick={(e, t) => handleHeaderClick(e, { index })}
                                className={getHeaderDepthClass(index, header.module_group)}>
<Menu.Item className={`${styles.shrinkOnHover} SubMenu__Item ${underlineMenu ? 'lighten-hover-border' : ''}${activeGroup === header.module_group ? ' is-active' : ''}`}
                                    style={{ width: '100%', display: 'inline-block' }}
                                >
                                    {/* Icon único (animado) */}
                                    <Icon
                                        name='dropdown'
                                        style={{
                                            marginRight: 6,
                                            transform: openHeaderIndices.includes(index) ? 'rotate(90deg)' : 'rotate(0deg)',
                                            opacity: openHeaderIndices.includes(index) ? 1 : 0,
                                            transition: 'opacity 600ms cubic-bezier(.2,.8,.2,1), transform 600ms cubic-bezier(.2,.8,.2,1)',
                                            pointerEvents: 'none',
                                        }}
                                    />
                                    {(() => {
                                        // obtener valor de icono por grupo (prioriza mapa de Icons.js)
                                        const key = normalizeGroup(header.module_group)
                                        const iconValue = groupIconMap.get(key) ?? header.icon ?? 'archive'
                                        return isImageIcon(iconValue) ? (
                                            <img
                                                src={iconValue}
                                                alt={header.module_group}
                                                style={{ width: 24, height: 24, verticalAlign: 'middle', marginRight: 8 }}
                                                className='SideBar__Outline'
                                            />) : (
                                            <Icon name={iconValue} size='large' className='SideBar__Outline' />
                                        )
                                    })()}
                                    {(expand == null || expand == 1) && (
                                        <h5
                                            className={`menu-row-title ${getHeaderDepthClass(index)}`}
                                            style={{ margin: 0 }}
                                        >
                                            {t(header.module_group)}
                                        </h5>
                                    )}
                                </Menu.Item>
                            </Accordion.Title>

                            <Transition visible={openHeaderIndices.includes(index)} animation='fade down' duration={220} unmountOnHide={false}>
                                <Accordion.Content key={`content-${header.module_group}-${index}`} style={{ marginTop: 0, paddingTop: 0 }} active={openHeaderIndices.includes(index)}>
                                    <Grid divided='vertically' style={{ width: '100%' }}>
                                        <Grid.Row style={{ paddingBottom: 0 }} />
                                        <Grid.Row style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 15 }}>
                                            <Grid.Column>
                                                <Menu vertical fluid text style={{ width: '100%' }}>
                                                    {(header.routes || []).map((route, indexR) => {
                                                        const active = isActiveRoute(route.url, header.module_group);
                                                        const hasChildren = Array.isArray(route.children) && route.children.length > 0;
                                                        const childRouteKey = `${header.module_group}-${route.name}`;
                                                        const isChildOpen = openChildRoutes.includes(childRouteKey);
                                                        const isAnyChildActive = hasChildren && route.children.some(c => isActiveRoute(c.url));

                                                        if (hasChildren) {
                                                            // Renderizar como sub-acordeón desplegable
                                                            return (
                                                                <Accordion key={`child-accordion-${indexR}`} fluid style={{ margin: 0, padding: 0 }}>
                                                                    <Accordion.Title
                                                                        active={isChildOpen}
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            setOpenChildRoutes(prev =>
                                                                                prev.includes(childRouteKey)
                                                                                    ? prev.filter(k => k !== childRouteKey)
                                                                                    : [...prev, childRouteKey]
                                                                            );
                                                                        }}
                                                                        style={{ padding: '4px 0', cursor: 'pointer' }}
                                                                    >
                                                                        <Menu.Item
                                                                            className={`${styles.growOnHover} ${isAnyChildActive ? 'is-active' : ''}`}
                                                                            style={{ margin: 0, display: 'flex', alignItems: 'center' }}
                                                                        >
                                                                            <Icon
                                                                                name='dropdown'
                                                                                style={{
                                                                                    marginRight: 4,
                                                                                    transform: isChildOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                                                                    transition: 'transform 300ms ease',
                                                                                    fontSize: '0.85em',
                                                                                }}
                                                                            />
                                                                            {route.icon ? (
                                                                                <Icon name={route.icon} size='small' style={{ marginRight: 6 }} />
                                                                            ) : null}
                                                                            <h5 style={{ marginLeft: 6, margin: 0, fontWeight: isAnyChildActive ? 700 : 400 }}>
                                                                                {route.name}
                                                                            </h5>
                                                                        </Menu.Item>
                                                                    </Accordion.Title>
                                                                    <Transition visible={isChildOpen} animation='fade down' duration={180} unmountOnHide={false}>
                                                                        <Accordion.Content active={isChildOpen} style={{ paddingTop: 0, paddingBottom: 0 }}>
                                                                            <Menu vertical fluid text style={{ width: '100%', paddingLeft: 12 }}>
                                                                                {route.children.map((child, childIdx) => {
                                                                                    const childActive = isActiveRoute(child.url);
                                                                                    return (
                                                                                        <Link
                                                                                            key={`child-link-${childIdx}-${child.url}`}
                                                                                            to={child.url}
                                                                                            className={`sub_menu_row ${isRouteInActiveTab(child.url) ? 'is-active' : ''} ${isRouteInOpenTab(child.url) && !isRouteInActiveTab(child.url) ? 'has-tab-open' : ''}`}
                                                                                            onClick={(e) => handleTabOpen(e, child)}
                                                                                        >
                                                                                            <Menu.Item
                                                                                                className={styles.growOnHover}
                                                                                                style={{ margin: 0, display: 'flex', alignItems: 'center', paddingLeft: 8 }}
                                                                                                onClick={() => handleClickLink(child)}
                                                                                            >
                                                                                                {child.icon ? (
                                                                                                    <Icon name={child.icon} size='small' style={{ marginRight: 6 }} />
                                                                                                ) : null}
                                                                                                <h5
                                                                                                    ref={(el) => { if (childActive) activeItemRef.current = el }}
                                                                                                    className={`menu-item-label${childActive ? ' is-active' : ''}`}
                                                                                                    style={{ marginLeft: 8, margin: 0, fontSize: '1.10em' }}
                                                                                                >
                                                                                                    {child.name}
                                                                                                </h5>
                                                                                            </Menu.Item>
                                                                                        </Link>
                                                                                    );
                                                                                })}
                                                                            </Menu>
                                                                        </Accordion.Content>
                                                                    </Transition>
                                                                </Accordion>
                                                            );
                                                        }

                                                        // Ruta normal sin children
                                                        return (
                                                            <Link
                                                                key={`link-${indexR}-${route.url}`}
                                                                to={route.url}
                                                                className={`sub_menu_row ${isRouteInActiveTab(route.url) ? 'is-active' : ''} ${isRouteInOpenTab(route.url) && !isRouteInActiveTab(route.url) ? 'has-tab-open' : ''} ${openHeaderIndices.includes(index) ? 'depth-parent' : ''}`}
                                                                onClick={(e) => handleTabOpen(e, route)}
                                                            >
                                                                <Menu.Item
                                                                    className={styles.growOnHover}
                                                                    style={{ margin: 0, display: 'flex', alignItems: 'center' }}
                                                                    onClick={() => handleClickLink(route)}
                                                                >
                                                                    {route.icon ? (
                                                                        <Icon name={route.icon} size='small' style={{ marginRight: 8 }} />
                                                                    ) : null}
                                                                    <h5
                                                                        ref={(el) => { if (active) activeItemRef.current = el }}
                                                                        className={`menu-item-label${active ? ' is-active' : ''}`}
                                                                        style={{ marginLeft: 10, margin: 0 }}
                                                                    >
                                                                        {t(route.name)}
                                                                    </h5>
                                                                </Menu.Item>
                                                            </Link>
                                                        );
                                                    })}

                                                </Menu>
                                            </Grid.Column>
                                        </Grid.Row>
                                    </Grid>
                                </Accordion.Content>
                            </Transition>
                        </Accordion>
                            : null))}
                </div>
            </Transition>
        </div>
    )
}

const mapStateToProps = (state) => ({
    client: state.app.client,
    lang: state.app.lang, // Añadido lang
})

const mapDispatchToProps = (dispatch) => ({
    getClientModules: () => dispatch(clientActions.getClientModules()),
    getWebClientPermissions: (clientId) => dispatch(clientActions.getWebClientPermissions(clientId)),
    actionClick: (data) => dispatch(clientActions.actionClick(data)),
})

export default connect(mapStateToProps, mapDispatchToProps)(ClientSidebarMenu)
